import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UUID } from "crypto";
import api from "@/app/api";
import {
  PartnerPreference,
  PartnerPreferenceCreate,
  PartnerPreferenceUpdate,
  PartnerPreferenceWithProduct,
} from "@/app/api/types/partnerPreferences";
import { partnerPreferencesKeys } from "@/query/keys/partnerPreferences";

/**
 * Get all preferences for a partner
 */
export function usePartnerPreferences(partnerId: UUID | null) {
  return useQuery<PartnerPreferenceWithProduct[]>({
    queryKey: partnerPreferencesKeys.list(partnerId!),
    queryFn: () => api.partnerPreferences.getPartnerPreferences(partnerId!),
    enabled: !!partnerId,
  });
}

/**
 * Get only general preferences for a partner
 */
export function useGeneralPreferences(partnerId: UUID | null) {
  return useQuery<PartnerPreference[]>({
    queryKey: partnerPreferencesKeys.general(partnerId!),
    queryFn: () => api.partnerPreferences.getGeneralPreferences(partnerId!),
    enabled: !!partnerId,
  });
}

/**
 * Get only product-specific preferences for a partner
 */
export function useProductPreferences(partnerId: UUID | null) {
  return useQuery<PartnerPreferenceWithProduct[]>({
    queryKey: partnerPreferencesKeys.products(partnerId!),
    queryFn: () => api.partnerPreferences.getProductPreferences(partnerId!),
    enabled: !!partnerId,
  });
}

/**
 * Create a new preference
 */
export function useCreatePreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      partnerId,
      data,
    }: {
      partnerId: UUID;
      data: PartnerPreferenceCreate;
    }) => api.partnerPreferences.postPartnerPreference(partnerId, data),
    onSuccess: (_, variables) => {
      // Invalidate all preference queries for this partner
      queryClient.invalidateQueries({
        queryKey: partnerPreferencesKeys.list(variables.partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerPreferencesKeys.general(variables.partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerPreferencesKeys.products(variables.partnerId),
      });
    },
  });
}

/**
 * Update a preference
 */
export function useUpdatePreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      preferenceId,
      data,
    }: {
      preferenceId: UUID;
      data: PartnerPreferenceUpdate;
    }) => api.partnerPreferences.putPreference(preferenceId, data),
    onSuccess: () => {
      // Invalidate all preference queries
      queryClient.invalidateQueries({
        queryKey: partnerPreferencesKeys.all,
      });
    },
  });
}

/**
 * Delete a preference
 */
export function useDeletePreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferenceId: UUID) =>
      api.partnerPreferences.deletePreference(preferenceId),
    onSuccess: () => {
      // Invalidate all preference queries
      queryClient.invalidateQueries({
        queryKey: partnerPreferencesKeys.all,
      });
    },
  });
}
